/**
 * PRO-6 — Order slice detail. THE MOST ANONYMITY-CRITICAL SCREEN IN THE PRODUCT.
 *
 * ─── WHAT IS PRESENT ───────────────────────────────────────────────────────────────────────
 *   • order_number, created_at
 *   • the SHARED order status — read-only. Producers may see it (it reveals no buyer identity)
 *     but are never NOTIFIED of changes; they see it only when they look (docs/04 §1.3, §5).
 *   • their OWN items: name, qty at box/pallet/truck, pieces
 *   • sums at their OWN BASE PRICES
 *   • the buyer's COUNTRY — the single buyer attribute a producer ever receives
 *   • the fixed instruction: "свяжитесь с MOST по заказу M-…"
 *
 * ─── WHAT IS ABSENT, BY DESIGN (docs/04 §5; backend asserts ANON-P-01..07) ──────────────────
 *   ✗ buyer identity — id, email, company, contact person, phone, city
 *   ✗ buyer_comment — it is the buyer's freeform note to Merdan and may contain self-identifying
 *     text ("call me on +998…"). Admin-only.
 *   ✗ any selling price, marked-up line total, or margin
 *   ✗ any trace of other producers — their lines, ids, names, prices, or even the total line
 *     count of the whole order
 *   ✗ WHOLE-ORDER AGGREGATES — truck_fill_pct, total order weight, order grand total. These are
 *     computed across every producer's items, so showing one producer the truck fill tells them
 *     other cargo exists and roughly how much of it.
 *
 * None of the above is "hidden" here. It is NOT IN `ProducerOrderSlice`, and `contract.ts` does
 * not declare it, so it cannot be rendered from this component — the absence is structural. The
 * only totals on this page are `slice_total_usd` and `slice_weight_kg`, which are this
 * producer's own slice and nothing else.
 *
 * ANON-P-07 asserts, server-side, the arithmetic identity
 *   slice_total_usd == Σ(qty_pieces × producer_price_per_piece_snapshot)
 * proving the selling price was not used even as an intermediate value.
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Info, ShieldCheck } from 'lucide-react';
import { Card, CardContent, ErrorState, FormSkeleton } from '@/components/ui';
import { OrderStatusBadge } from '@/components/StatusBadge';
import { qk } from '@/shared/queryClient';
import * as api from '@/shared/api';
import { ApiError } from '@/shared/http';
import { formatDateTime, formatInt, formatKg, formatUsd } from '@/shared/money';

export function OrderSliceScreen() {
  const { number = '' } = useParams<{ number: string }>();
  const { t, i18n } = useTranslation();

  const query = useQuery({
    queryKey: qk.order(number),
    queryFn: () => api.getOrderSlice(number),
  });

  if (query.isLoading) return <FormSkeleton fields={4} />;

  // A 404 here means either "no such order" OR "that order contains none of your products" — the
  // backend deliberately does not distinguish them (existence is itself information, CLAUDE.md).
  // So the copy must not either.
  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    if (notFound) {
      return (
        <div className="mx-auto max-w-7xl">
          <BackLink />
          <Card className="mt-4">
            <CardContent className="p-8 text-center">
              <p className="text-base font-medium text-foreground">{t('orders.notFound')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('orders.notFoundBody')}</p>
            </CardContent>
          </Card>
        </div>
      );
    }
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const slice = query.data!;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <BackLink />

      {/* ── Header: number, date, shared status, buyer COUNTRY ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight tabular-nums text-foreground">
            {t('orders.detailTitle', { number: slice.order_number })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatDateTime(slice.created_at, i18n.language)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Read-only. Only the admin moves an order between states. */}
          <OrderStatusBadge status={slice.status} />
        </div>
      </div>

      {/* The ONLY buyer attribute on this screen — and in this app. */}
      <Card>
        <CardContent className="flex items-center justify-between gap-4 p-4">
          <span className="text-sm text-muted-foreground">{t('orders.buyerCountry')}</span>
          <span className="text-sm font-medium text-foreground">{slice.buyer_country}</span>
        </CardContent>
      </Card>

      {/* ── Own items, at own base prices ── */}
      <Card className="overflow-hidden">
        <div className="border-b border-border bg-muted/60 px-5 py-3">
          <h2 className="text-sm font-semibold text-foreground">{t('orders.yourItems')}</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-sm">
            <caption className="sr-only">{t('orders.yourItems')}</caption>
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="px-5 py-2.5 font-medium">
                  {t('orders.product')}
                </th>
                <th scope="col" className="px-5 py-2.5 font-medium">
                  {t('orders.qty')}
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-medium">
                  {t('orders.pieces')}
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-medium">
                  {t('orders.pricePerPiece')}
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-medium">
                  {t('orders.lineWeight')}
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-medium">
                  {t('orders.lineTotal')}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-hairline">
              {slice.items.map((line) => (
                <tr key={line.product_id}>
                  <td className="px-5 py-3 font-medium text-foreground">{line.product_name}</td>
                  <td className="px-5 py-3 text-muted-foreground">
                    {/* Buyers order in box/pallet/truck — never piece or pack (openapi QtyUnit). */}
                    {line.qty_input} {t(`units.${line.qty_unit}`)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                    {formatInt(line.qty_pieces)}
                  </td>
                  {/* The producer's OWN base price snapshot. Never a selling price. */}
                  <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                    {formatUsd(line.price_per_piece)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">
                    {formatKg(line.line_weight_kg)}
                  </td>
                  <td className="px-5 py-3 text-right font-medium tabular-nums text-foreground">
                    {formatUsd(line.line_total_usd)}
                  </td>
                </tr>
              ))}
            </tbody>

            {/*
              SLICE totals only. There is deliberately no order grand total, no whole-order
              weight, and no truck-fill gauge on this page — those are computed across every
              producer's items and would betray the existence and size of other cargo.
            */}
            <tfoot className="border-t-2 border-border bg-muted/60">
              <tr>
                <td colSpan={4} className="px-5 py-3 text-right text-sm text-muted-foreground">
                  {t('orders.sliceWeight')}
                </td>
                <td className="px-5 py-3 text-right tabular-nums text-foreground/80">
                  {formatKg(slice.slice_weight_kg)}
                </td>
                <td />
              </tr>
              <tr>
                <td colSpan={5} className="px-5 py-3 text-right text-sm font-medium text-foreground/80">
                  {t('orders.totalAtYourPrices')}
                </td>
                <td className="px-5 py-3 text-right text-base font-semibold tabular-nums text-foreground">
                  {formatUsd(slice.slice_total_usd)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* ── The fixed instruction. `instruction` is an i18n KEY (`contact_most`), not a rendered
             string — the server sends the key so the producer reads it in their own language. ── */}
      <Card className="border-primary/50 bg-primary-muted">
        <CardContent className="flex items-start gap-3 p-5">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-link" aria-hidden />
          <div>
            <p className="text-sm font-medium text-foreground">
              {t(`orders.instruction_${slice.instruction}`, { number: slice.order_number })}
            </p>
            <p className="mt-1 text-sm text-foreground/80">{t('orders.instructionHint')}</p>
          </div>
        </CardContent>
      </Card>

      {/*
        The anonymity note. This is not legalese — it is the screen explaining its own emptiness.
        A producer who sees no buyer name might assume the system is broken, or go looking. Told
        plainly that the shielding is mutual (their price is hidden from the buyer just as the
        buyer is hidden from them), the absence reads as the product working.
      */}
      <div className="flex items-start gap-3 rounded-md border border-border bg-card px-4 py-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden />
        <p className="text-xs text-muted-foreground">{t('orders.anonymityNote')}</p>
      </div>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Link
      to="/orders"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      {t('orders.title')}
    </Link>
  );
}
